import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  ErrorModal,
  Preloader,
  Text,
  useResponsive,
  WarningIcon,
} from '@common-fe/common-fe';

import { WarnModal } from '@/components/elements';

import { useCIPInfo } from '../hooks';
import useGetPlanCIPConfigQuery from '../queries/useGetPlanCIPConfig.query';
import { useCIPStatusUpdateQuery } from '../Questionnaire/queries';
import QuestionaireWrapper from '../Questionnaire/QuestionnaireWrapper';

import CIPConfirmationModal from './CIPConfirmationModal';

const Banner = () => {
  const [isConfirmationModalVisible, setConfirmationModalVisible] = useState(false);
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { invalidateQuery: invalidateEnrollmentsQuery } = useCIPStatusUpdateQuery();
  const [isSuccessModal, setSuccessModal] = useState(false);
  const [isErrorModal, setErrorModal] = useState(false);
  const {
    isVerificationRequired,
    setServerErrorShowed,
    handleFailedCip,
    invalidateCIPInfoQuery,
    handleVerify,
    isVerificationLoading,
    isFailed,
    isServerErrorShowed,
  } = useCIPInfo({
    onFail: () => {
      setErrorModal(true);
    },
    onSuccess: async () => {
      setSuccessModal(true);

      invalidateEnrollmentsQuery();
      invalidateCIPInfoQuery();
    },
  });
  useEffect(() => {
    if (isErrorModal) {
      handleFailedCip();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isErrorModal]);
  const handleRedirect = useCallback(() => {
    setSuccessModal(false);
    setErrorModal(false);
  }, []);
  const { data } = useGetPlanCIPConfigQuery({
    enabled: isVerificationRequired,
  });
  const content = useMemo(() => {
    if (!isVerificationRequired) {
      return null;
    }

    return (
      <Box
        pad={{ vertical: 'l', horizontal: 'xl' }}
        data-testid="cip-banner"
        margin={{ top: 'l' }}
        round="container2Round"
        direction="column"
        justify="center"
        background="warningContainer"
        border={{ color: 'warningBorder' }}
      >
        {isFailed && (
          <ErrorModal
            testId="questionnaire-wrapper_error"
            visible={isErrorModal}
            onSetVisible={setErrorModal}
            header={t("We're Sorry, we're unable to open your HSA")}
            helpText={t(`We cannot verify your details with the information provided.
          Please contact your administrator for next steps.`)}
            buttonText={t('Got It!')}
            onSubmit={() => setErrorModal(false)}
          />
        )}
        <WarnModal
          visible={isServerErrorShowed}
          testId="CIP-banner_server_error"
          onSetVisible={setServerErrorShowed}
          header={t('The server encountered an error processing the request')}
          helptext={t(`It doesn't appear to have affected your data, but we cannot verify your identity to establish your HSA.
      Our technical staff have been automatically notified and will be looking into this with the utmost urgency.`)}
          buttonText={t('Close')}
          onSubmit={handleVerify}
          submitButtonText={t('Try Again')}
        />
        <Box direction={isMobile ? 'column' : 'row'} align={isMobile ? 'start' : 'center'}>
          <Box fill margin={isMobile ? { bottom: 'spacing12' } : {}}>
            <Box direction="row" round="container1Round" align="center">
              <WarningIcon size="24px" />
              <Text
                textAlign={isMobile ? 'start' : 'center'}
                color="textBody"
                margin={{ left: 'xs' }}
                size="large"
                weight="bold"
              >
                {t('We need to verify your identity to establish your HSA')}
              </Text>
            </Box>

            <Text color="textBody" size="medium" margin={{ top: 'xs' }}>
              {t('With just a few extra details we can activate your account.')}
            </Text>
          </Box>
          <AppButton
            testId="cip-banner_verify"
            onClick={() => setConfirmationModalVisible(true)}
            disabled={isVerificationLoading}
            width="xregular"
          >
            {isVerificationLoading ? <Preloader /> : t('Verify Identity')}
          </AppButton>
        </Box>
      </Box>
    );
  }, [
    isMobile,
    handleVerify,
    isErrorModal,
    isFailed,
    isServerErrorShowed,
    isVerificationLoading,
    isVerificationRequired,
    setServerErrorShowed,
    t,
  ]);

  return (
    <QuestionaireWrapper isSuccessModal={isSuccessModal} onCloseModal={handleRedirect} isStandAloneMode>
      <CIPConfirmationModal
        buttonNode={
          <AppButton
            fill
            testId="cip-banner_start-verify"
            onClick={() => handleVerify()}
            style={{ height: '54px' }}
            disabled={isVerificationLoading}
          >
            {isVerificationLoading ? <Preloader /> : t('Start Verification')}
          </AppButton>
        }
        closureDelayDays={data?.CIPAccountDelayDays}
        isVisible={isConfirmationModalVisible}
        onSetVisible={setConfirmationModalVisible}
      />

      {content}
    </QuestionaireWrapper>
  );
};
export default Banner;
